// Splash Screen Video Player - Fixed cleanup to remove black box
import { setIsBuffering } from "../state";

let playerInstance = null;
let shakaPlayer = null;
let currentMode = "web";
let playerReady = false;
let playerState = "IDLE";
let destroyTimeout = null;

// Detect Android TV by user agent
function isAndroidTV() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  const isAndroid = ua.includes("android");
  const isTV =
    ua.includes("tv") || ua.includes("androidtv") || window.innerWidth >= 1920;

  console.log("🔍 Splash - Platform detection:", {
    isAndroid,
    isTV,
    userAgent: ua,
    screenWidth: window.innerWidth,
  });

  return isAndroid && isTV;
}

// Load Shaka Player if not already loaded
async function loadShakaPlayer() {
  if (window.shaka) {
    console.log("✅ Shaka Player already loaded");
    return Promise.resolve();
  }

  console.log("📥 Loading Shaka Player...");

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    // 👇 Using compiled version WITHOUT UI
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.8/shaka-player.compiled.js";

    script.onload = () => {
      console.log("✅ Shaka Player loaded successfully");
      if (window.shaka && window.shaka.polyfill) {
        window.shaka.polyfill.installAll();
      }
      resolve();
    };

    script.onerror = () => {
      console.error("❌ Failed to load Shaka Player");
      reject(new Error("Failed to load Shaka Player"));
    };

    document.head.appendChild(script);
  });
}

const splashVideoPlayer = {
  async destroy() {
    try {
      console.log("🧹 Splash - Destroying video player, mode:", currentMode);

      // Clear any pending destroy timeout
      if (destroyTimeout) {
        clearTimeout(destroyTimeout);
        destroyTimeout = null;
      }

      playerReady = false;
      playerState = "IDLE";

      // Destroy Shaka Player FIRST
      if (shakaPlayer) {
        try {
          console.log("🧹 Destroying Shaka Player instance...");
          await shakaPlayer.destroy();
          shakaPlayer = null;
          console.log("✅ Shaka Player destroyed");
        } catch (e) {
          console.warn("⚠️ Shaka Player destroy warning:", e);
          shakaPlayer = null; // Force null even on error
        }
      }

      // Destroy video element COMPLETELY
      if (playerInstance) {
        try {
          console.log("🧹 Removing video element from DOM...");

          // Stop playback
          playerInstance.pause();

          // Clear source
          playerInstance.removeAttribute("src");
          playerInstance.src = "";

          // Unload
          playerInstance.load();

          // Remove all event listeners by cloning
          const clone = playerInstance.cloneNode(false);
          if (playerInstance.parentNode) {
            playerInstance.parentNode.replaceChild(clone, playerInstance);
          }

          // Remove from DOM
          if (clone.parentNode) {
            clone.parentNode.removeChild(clone);
          }

          playerInstance = null;
          console.log("✅ Video element completely removed from DOM");
        } catch (e) {
          console.warn("⚠️ Video element removal warning:", e);
          // Force removal even on error
          if (playerInstance && playerInstance.parentNode) {
            playerInstance.parentNode.removeChild(playerInstance);
          }
          playerInstance = null;
        }
      }

      // Extra cleanup: Remove any orphaned video elements
      try {
        const orphanedVideos = document.querySelectorAll("video");
        console.log(`🔍 Found ${orphanedVideos.length} video elements in DOM`);

        orphanedVideos.forEach((video, index) => {
          // Only remove if it looks like our splash video
          if (
            video.style.zIndex === "9999" ||
            video.style.position === "fixed"
          ) {
            console.log(`🧹 Removing orphaned video element ${index}`);
            video.pause();
            video.src = "";
            if (video.parentNode) {
              video.parentNode.removeChild(video);
            }
          }
        });
      } catch (e) {
        console.warn("⚠️ Orphan cleanup warning:", e);
      }

      setIsBuffering(false);
      currentMode = "web";
      console.log("✅ Splash - Player destroyed successfully");
      console.log(
        "🔍 Remaining video elements:",
        document.querySelectorAll("video").length
      );
    } catch (error) {
      console.error("❌ Splash - Error destroying player:", error);
      // Force cleanup even on error
      playerInstance = null;
      shakaPlayer = null;
      setIsBuffering(false);
    }
  },

  async playVideo(streamUrl, context = "splash") {
    console.log("=== Splash playVideo called ===", {
      streamUrl,
      context,
      isAndroidTV: isAndroidTV(),
    });

    try {
      // Clean up any existing player first
      await this.destroy();

      setIsBuffering(true);
      playerState = "LOADING";

      // Decide which player to use
      const useShaka = isAndroidTV();

      if (useShaka) {
        // ===== SHAKA PLAYER PATH (for Android TV) =====
        console.log("📱 Splash - Using Shaka Player for Android TV");
        return await this.playShakaVideo(streamUrl);
      } else {
        // ===== WEB VIDEO ELEMENT PATH (for browsers) =====
        console.log("🌐 Splash - Using web video element");
        return await this.playWebVideo(streamUrl);
      }
    } catch (error) {
      console.error("=== Splash playVideo failed ===", error);
      setIsBuffering(false);
      playerState = "ERROR";

      // Try fallback to web video if Shaka fails
      if (currentMode === "shaka") {
        console.log("⚠️ Shaka failed, trying web video fallback...");
        try {
          return await this.playWebVideo(streamUrl);
        } catch (fallbackError) {
          console.error("❌ Fallback also failed:", fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  async playShakaVideo(streamUrl) {
    console.log("📱 Starting Shaka Player initialization");
    currentMode = "shaka";

    try {
      // Load Shaka Player library
      await loadShakaPlayer();

      // Check if browser is supported
      if (!window.shaka.Player.isBrowserSupported()) {
        console.warn(
          "⚠️ Browser not supported by Shaka, falling back to web video"
        );
        return await this.playWebVideo(streamUrl);
      }

      // Create video element
      playerInstance = document.createElement("video");
      playerInstance.setAttribute("playsinline", "");
      playerInstance.setAttribute("webkit-playsinline", "");
      playerInstance.setAttribute("data-splash-video", "true"); // Mark it for cleanup
      playerInstance.autoplay = true;
      playerInstance.muted = true;
      playerInstance.controls = false;

      // Full screen styling for splash
      playerInstance.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        object-fit: contain;
        z-index: 9999;
        background-color: #000000;
      `;

      // Add to DOM
      document.body.insertBefore(playerInstance, document.body.firstChild);
      console.log("✅ Video element created and added to DOM");

      // Create Shaka Player instance
      shakaPlayer = new window.shaka.Player(playerInstance);

      // Setup event listeners
      shakaPlayer.addEventListener("error", (event) => {
        console.error("📱 Shaka Player error:", event.detail);
        playerState = "ERROR";
        setIsBuffering(false);
      });

      // Video element events
      playerInstance.addEventListener("loadedmetadata", () => {
        console.log("📱 Shaka - Metadata loaded", {
          duration: playerInstance.duration,
          videoWidth: playerInstance.videoWidth,
          videoHeight: playerInstance.videoHeight,
        });
      });

      playerInstance.addEventListener("canplay", () => {
        console.log("📱 Shaka - Can play");
        playerReady = true;
        setIsBuffering(false);
      });

      playerInstance.addEventListener("playing", () => {
        console.log("📱 Shaka - Playing");
        playerState = "PLAYING";
        setIsBuffering(false);
      });

      playerInstance.addEventListener("waiting", () => {
        console.log("📱 Shaka - Buffering");
        setIsBuffering(true);
      });

      playerInstance.addEventListener("ended", () => {
        console.log("📱 Shaka - Video ended");
        playerState = "ENDED";
        // Auto cleanup after video ends with slight delay
        destroyTimeout = setTimeout(() => {
          console.log("🧹 Auto-cleanup triggered after video ended");
          this.destroy();
        }, 200);
      });

      playerInstance.addEventListener("error", (e) => {
        const error = playerInstance.error;
        console.error("📱 Shaka video element error:", {
          code: error?.code,
          message: error?.message,
        });
        playerState = "ERROR";
        setIsBuffering(false);
      });

      // Configure Shaka Player for better Android TV performance
      const config = {
        streaming: {
          bufferingGoal: 30,
          rebufferingGoal: 2,
          bufferBehind: 30,
        },
        abr: {
          enabled: true,
        },
      };
      shakaPlayer.configure(config);

      console.log("📱 Loading video with Shaka Player:", streamUrl);

      // Load the video
      await shakaPlayer.load(streamUrl);
      console.log("✅ Shaka Player loaded video successfully");

      // Start playback
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn("⚠️ Playback start timeout");
          resolve(); // Still resolve to not block
        }, 10000);

        const handlePlaying = () => {
          clearTimeout(timeout);
          playerInstance.removeEventListener("playing", handlePlaying);
          playerInstance.removeEventListener("error", handleError);
          console.log("✅ Shaka playback started successfully");
          resolve();
        };

        const handleError = (e) => {
          clearTimeout(timeout);
          playerInstance.removeEventListener("playing", handlePlaying);
          playerInstance.removeEventListener("error", handleError);
          reject(new Error("Playback failed"));
        };

        playerInstance.addEventListener("playing", handlePlaying, {
          once: true,
        });
        playerInstance.addEventListener("error", handleError, { once: true });

        // Trigger play
        playerInstance
          .play()
          .then(() => {
            console.log("✅ Play() resolved");
          })
          .catch((playError) => {
            console.error("❌ Play() rejected:", playError);
            // Try muted playback
            playerInstance.muted = true;
            playerInstance
              .play()
              .then(() => console.log("✅ Muted play successful"))
              .catch((mutedError) => {
                console.error("❌ Even muted play failed:", mutedError);
                handleError(mutedError);
              });
          });
      });
    } catch (error) {
      console.error("❌ Shaka Player initialization failed:", error);
      playerState = "ERROR";
      setIsBuffering(false);
      throw error;
    }
  },

  async playWebVideo(streamUrl) {
    console.log("🌐 Splash - Using web video element");
    currentMode = "web";

    // Create video element
    playerInstance = document.createElement("video");
    playerInstance.setAttribute("playsinline", "");
    playerInstance.setAttribute("webkit-playsinline", "");
    playerInstance.setAttribute("data-splash-video", "true"); // Mark it for cleanup
    playerInstance.preload = "auto";
    playerInstance.muted = true;
    playerInstance.autoplay = true;

    // Full screen styling for splash
    playerInstance.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      object-fit: contain;
      z-index: 9999;
      background-color: #000000;
    `;

    // Event listeners
    playerInstance.addEventListener("loadedmetadata", () => {
      console.log("🌐 Splash - Metadata loaded", {
        duration: playerInstance.duration,
        videoWidth: playerInstance.videoWidth,
        videoHeight: playerInstance.videoHeight,
      });
    });

    playerInstance.addEventListener("canplay", () => {
      console.log("🌐 Splash - Can play");
      setIsBuffering(false);
      playerReady = true;
    });

    playerInstance.addEventListener("playing", () => {
      console.log("🌐 Splash - Playing");
      playerState = "PLAYING";
      setIsBuffering(false);
    });

    playerInstance.addEventListener("ended", () => {
      console.log("🌐 Splash - Video ended");
      playerState = "ENDED";
      // Auto cleanup after video ends with slight delay
      destroyTimeout = setTimeout(() => {
        console.log("🧹 Auto-cleanup triggered after video ended");
        this.destroy();
      }, 200);
    });

    playerInstance.addEventListener("error", (e) => {
      const error = playerInstance.error;
      console.error("🌐 Splash - Video Error:", {
        code: error?.code,
        message: error?.message,
      });
      playerState = "ERROR";
      setIsBuffering(false);
    });

    playerInstance.addEventListener("waiting", () => {
      console.log("🌐 Splash - Waiting/Buffering");
      setIsBuffering(true);
    });

    // Add to DOM
    document.body.insertBefore(playerInstance, document.body.firstChild);
    console.log("✅ Splash - Video element added to DOM");

    // Set source and load
    playerInstance.src = streamUrl;
    playerInstance.load();

    // Wait for video to be ready
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Video load timeout"));
      }, 10000);

      const handleCanPlay = () => {
        clearTimeout(timeout);
        playerInstance.removeEventListener("canplay", handleCanPlay);
        playerInstance.removeEventListener("error", handleError);

        // Start playback
        playerInstance
          .play()
          .then(() => {
            console.log("✅ Splash - Playback started");
            resolve();
          })
          .catch((playError) => {
            console.error("❌ Splash - Playback failed:", playError);
            // Try muted playback
            playerInstance.muted = true;
            playerInstance
              .play()
              .then(() => {
                console.log("✅ Splash - Playback started (muted)");
                resolve();
              })
              .catch((mutedError) => {
                console.error(
                  "❌ Splash - Muted playback also failed:",
                  mutedError
                );
                reject(mutedError);
              });
          });
      };

      const handleError = (e) => {
        clearTimeout(timeout);
        playerInstance.removeEventListener("canplay", handleCanPlay);
        playerInstance.removeEventListener("error", handleError);
        reject(new Error("Video load error"));
      };

      playerInstance.addEventListener("canplay", handleCanPlay, { once: true });
      playerInstance.addEventListener("error", handleError, { once: true });
    });
  },

  getState() {
    return {
      ready: playerReady,
      state: playerState,
      currentMode: currentMode,
      isAndroidTV: isAndroidTV(),
      usingShaka: !!shakaPlayer,
      videoElementsInDOM: document.querySelectorAll("video").length,
    };
  },

  // Force cleanup method (call this if needed)
  forceCleanup() {
    console.log("🧨 Force cleanup called");
    const allVideos = document.querySelectorAll("video");
    console.log(`🔍 Found ${allVideos.length} video elements to remove`);

    allVideos.forEach((video, index) => {
      console.log(`🧹 Removing video element ${index}`);
      video.pause();
      video.src = "";
      if (video.parentNode) {
        video.parentNode.removeChild(video);
      }
    });

    playerInstance = null;
    shakaPlayer = null;
    console.log("✅ Force cleanup complete");
  },
};

export default splashVideoPlayer;
